import React from 'react';
import { compose, withHandlers, withPropsOnChange, withProps } from 'recompose';
import { connect } from 'react-redux';
import Joi from 'joi-full';
import { reduxForm, formValueSelector } from 'redux-form';
import { get, isEqual, trim } from 'lodash';
import moment from 'moment';
import { t } from '../../../../modules/infra/i18n';
import { withActionByRole } from '../../../common/helpers/actions';
import withNotifications from '../../../common/withNotifications';
import CloseCashRegisterComponent from '../components/CloseCashRegister';
import withToggle from '../../../common/hoc/withToggle';
import { closeShopCashRegister, closeBarberCashRegister } from '../actions';
import { syncValidateData } from '../../../common/validator';
import { getActiveRegisterCurrentBalance, getActiveCashRegister } from '../selectors';
import { trimData } from '../../../common/trimData';
import config from '../../../../config';
import {
  getInitialDenominationValues,
  getBillCountsTotal,
  getBillCountsDescription,
} from '../helpers';
import withCountBills from './withCountBills';
import confirmModal from '../../../common/confirmModal';
import Money from '../../../common/money';

const CLOSE_REGISTER_FORM = 'close-cash-register';

const schema = Joi.object().keys({
  notes: Joi.string().optional(),
  actualEndBalance: Joi.number().optional(),
  countBills: Joi.boolean().optional(),
  billCounts: Joi.array().items(
    Joi.object().keys({
      denomination: Joi.number(),
      billsNo: Joi.number().optional(),
      isCoin: Joi.boolean().optional(),
      name: Joi.string().optional(),
      coinsPerRoll: Joi.number().optional(),
    })
  ),
});

const formSelector = formValueSelector(CLOSE_REGISTER_FORM);
const form = reduxForm({
  form: CLOSE_REGISTER_FORM,
  validate: values => syncValidateData(values, schema),
});

const CloseCashRegister = compose(
  withActionByRole('closeCashRegister', closeShopCashRegister, closeBarberCashRegister),
  withNotifications,
  withToggle('modal'),
  withHandlers({
    onSubmit: props => async (values) => {
      const formValues = trimData(values);

      const confirmed = await confirmModal({
        type: 'yesno',
        title: t('register:cash_register.close_register_confirm_title'),
        message: (
          <div className="u-fontsize-normal">
            {t('register:cash_register.close_register_confirm_message_part1')}
            {' '}<Money>{formValues.actualEndBalance}</Money>{' '}
            {t('register:cash_register.close_register_confirm_message_part2')}
          </div>
        ),
        confirmButtonText: t('common:buttons.yes'),
        negativeButtonText: t('common:buttons.no'),
      });

      if (confirmed) {
        if (formValues.countBills) {
          formValues.notes = trim(`${formValues.notes || ''}\n${getBillCountsDescription(formValues.billCounts)}`);
        }

        props.closeCashRegister(formValues).then(() => props.addNotification({
          title: t('common:notifications.success_title_alt'),
          level: 'success',
          message: t('register:cash_register.register_closed_success'),
        }));
      }
    },
  }),
  withCountBills,
  connect(state => ({
    expectedBalance: getActiveRegisterCurrentBalance(state),
    register: getActiveCashRegister(state),
    countBills: formSelector(state, 'countBills'),
    billCounts: formSelector(state, 'billCounts'),
    actualEndBalance: formSelector(state, 'actualEndBalance'),
  })),
  withProps(props => ({
    initialValues: {
      actualEndBalance: props.expectedBalance,
      countBills: props.countBillsByDefault.whenClosing,
      billCounts: getInitialDenominationValues(),
    },
    showCountBillsToggle: props.suggestCountingBills.whenClosing,
  })),
  withProps((props) => {
    const transactions = get(props, 'register.transactions', []);
    const openedAt = get(props, 'register.openedAt');
    const isPayin = ({ desc }) => desc.toLowerCase().startsWith('payin');
    const isPayout = ({ desc }) => desc.toLowerCase().startsWith('payout');
    const getSum = transactionsToSum => transactionsToSum.reduce((acc, t) => acc + t.amount, 0);

    const paidOutTransactions = transactions.filter(isPayout);
    const paidInTransactions = transactions.filter(isPayin);
    const cashSales = transactions.filter(transaction => !isPayout(transaction) && !isPayin(transaction));

    return {
      paidOut: getSum(paidOutTransactions),
      cashSales: getSum(cashSales),
      paidIn: getSum(paidInTransactions),
      openedBy: get(props, 'register.openedBy.username', ''),
      openedAt: `${moment(openedAt).format(config.dateFormat)}, ${moment(openedAt).format(config.timeFormat)}`,
    };
  }),
  form,
  withPropsOnChange(
    (props, nextProps) => (
      !isEqual(props.billCounts, nextProps.billCounts)
      || props.countBills !== nextProps.countBills
    ),
    (props) => {
      if (props.countBills && !!props.billCounts) {
        props.change('actualEndBalance', getBillCountsTotal(props.billCounts));
      }
    },
  ),
);

export default CloseCashRegister(CloseCashRegisterComponent);
